import { Suspense, useEffect, useMemo, useRef } from 'react'

import { useThemeContext } from '@space-lib/hooks'
import ErrorBoundary from '@space-shell/common/components/errorBoundary'
import useMembership from '@space-shell/common/hooks/useMembership'

import { useAuthStore, useUserInfoStore } from '@space-shell/core/store'

import type { IMembershipChatInjector } from '@space-shell/modules/membershipChatConsumer/types'

import { useMembershipChatInjector } from './hooks'

const MembershipChatConsumer = ({ ...props }: JSX.IntrinsicElements['div']) => {
  const ref = useRef<HTMLDivElement>(null)
  const [error, MembershipChatInjector] = useMembershipChatInjector()
  const { membershipData, membershipSlug } = useMembership()
  const { theme } = useThemeContext()
  const { id, token, authorities, userName, ucode, locale } = useAuthStore()
  const { avatar, email } = useUserInfoStore()

  const user = useMemo(() => {
    return {
      id,
      token,
      authorities,
      name: userName,
      email: email ?? '',
      avatar: avatar,
      ucode
    }
  }, [authorities, avatar, email, id, token, ucode, userName])

  useEffect(() => {
    if (error || !MembershipChatInjector || !ref.current) return

    const renderNode = ref.current

    let chatAppInjector: IMembershipChatInjector | null = null

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      chatAppInjector = new MembershipChatInjector(renderNode)

      chatAppInjector?.mount({
        language: locale,
        membership: membershipData,
        membershipSlug,
        theme,
        token,
        user
      })
    } catch {
      // pass
    }

    return () => {
      chatAppInjector?.unmount()
    }
  }, [error, MembershipChatInjector, locale, membershipData, membershipSlug, theme, user, token])

  if (error) return null

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <div
          {...props}
          ref={ref}
          className='membership-chat-consumer-mfe _text-gray-900 flex items-center'
        />
      </Suspense>
    </ErrorBoundary>
  )
}

export default MembershipChatConsumer
